import { Space, Flex, Typography } from "antd";
import bem from "easy-bem";
import { usePersonBlock } from "features/cases/blocks/CommonCard/elements/PersonBlock/usePersonBlock";
import { useCommentHashHighlight } from "features/comments/elements/Comment/hooks/useCommentHashHighlight";
import { buildShortLink } from "features/links/utils/shortLink";
import ActionToggle from "features/social-toggles/blocks/ActionToggle";
import { useFlag } from "hooks/useFlags";
import { useUser } from "hooks/useUser";
import React, { useEffect, useState } from "react";
import { Meta } from "types/basic";
import DateDisplay from "ui/DateDisplay";
import ShareLink from "ui/ShareLink";

import "./Comment.css";

const cn = bem("Comment");
const { Text } = Typography;

type CommentProps = {
  id: number;
  text: string;
  meta: Meta;
  mentionedPeople?: string[];
  hasChildren?: boolean;
  onDeleteToggle?: (isDeleted: boolean, recursive: boolean) => void;
  allowModeratorActions?: boolean;
  problemId?: string;
  solutionId?: string;
  cardId?: string;
};

const Comment: React.FC<CommentProps> = ({
  id,
  text,
  meta,
  mentionedPeople = [],
  hasChildren = false,
  onDeleteToggle,
  allowModeratorActions = true,
  problemId,
  solutionId,
  cardId,
}) => {
  const { isModerator, userId } = useUser();
  const moderationEnabled = useFlag("moderationEnabled");
  const canModerate = isModerator && moderationEnabled && allowModeratorActions;
  const isHighlighted = useCommentHashHighlight(id);
  const shareUrl = problemId
    ? buildShortLink({ kind: "comment", id, problemId, solutionId, cardId })
    : undefined;

  const isDeleted = meta?.isDeleted ?? false;

  const [isDeletedCurrent, setIsDeletedCurrent] = useState(isDeleted);

  useEffect(() => {
    setIsDeletedCurrent(isDeleted);
  }, [isDeleted]);

  const isAuthor =
    userId &&
    (typeof meta?.user === "object"
      ? meta?.user?.id === userId
      : meta?.user === userId);
  const canSeeOriginal = isModerator || isAuthor;

  const statusText = isDeletedCurrent ? "Комментарий удалён модератором" : null;

  const { avatarElement, personBlock } = usePersonBlock(meta, false);

  return (
    <div
      id={`comment-${id}`}
      className={cn({ hasChildren, highlighted: isHighlighted })}
    >
      <Space
        direction="horizontal"
        style={{ alignItems: "flex-start" }}
      >
        {avatarElement}
        <Flex
          vertical={true}
          style={{ flex: 1 }}
        >
          <Space
            direction="horizontal"
            size="small"
          >
            {personBlock}
            <Text type="secondary">
              <DateDisplay
                dateModified={meta?.dateModified}
                dateCreated={meta?.dateCreated}
                mode="created"
              />
            </Text>
            {shareUrl && (
              <ShareLink
                url={shareUrl}
                elementName="комментарий"
              />
            )}
          </Space>
          <div className="comment-content">
            {mentionedPeople.length > 0 && (
              <div style={{ marginBottom: 6 }}>
                <Text type="secondary">
                  Упомянуты:{" "}
                  {mentionedPeople.map((name) => `@${name}`).join(", ")}
                </Text>
              </div>
            )}
            {statusText && (
              <>
                <Text strong>{statusText}</Text>
                {canSeeOriginal && ": "}
              </>
            )}
            {(!statusText || canSeeOriginal) && text}
          </div>
          {canModerate && (
            <Space
              direction="horizontal"
              size="small"
              style={{ marginTop: 8 }}
            >
              <ActionToggle
                kind="delete"
                objectType="comment"
                objectId={String(id)}
                isActive={isDeletedCurrent}
                onToggle={() => {
                  const newIsDeleted = !isDeletedCurrent;
                  setIsDeletedCurrent(newIsDeleted);
                  onDeleteToggle?.(newIsDeleted, false);
                }}
              />
              {hasChildren && (
                <ActionToggle
                  kind="deleteRecursive"
                  objectType="comment"
                  objectId={String(id)}
                  isActive={isDeletedCurrent}
                  recursive={true}
                  onToggle={() => {
                    const newIsDeleted = !isDeletedCurrent;
                    setIsDeletedCurrent(newIsDeleted);
                    onDeleteToggle?.(newIsDeleted, true);
                  }}
                />
              )}
              {/* <ActionToggle
                kind="moderate"
                objectType="comment"
                objectId={String(id)}
                isActive={isModeratedCurrent}
                onToggle={() => {
                  _setIsModeratedCurrent(!isModeratedCurrent);
                }}
              /> */}
            </Space>
          )}
        </Flex>
      </Space>
    </div>
  );
};

export default Comment;
